import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { App } from "antd";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseTextArea from "@/components_v2/FormFields/Input/BaseTextArea";
import { useDecideModerationCaseMutation } from "@/redux/service";
import type { DecideCaseRequest } from "@/redux/moderation/types";
import BanUserModal from "@/domains/moderation/components/BanUserModal";
import LiftBanModal from "@/domains/moderation/components/LiftBanModal";
import { useActiveBan } from "@/domains/moderation/hooks/useActiveBan";
import { CaseDecisionProps } from "@/features/community/moderation/types";
import { MODERATION_STATES } from "@/domains/moderation/constants";

import "../styles.css";

const NOT_OPEN_STATUS = 409;

const CaseActions = ({ moderationCase }: CaseDecisionProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const feedbackId = useId();
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isBanOpen, setIsBanOpen] = useState(false);
  const [isLiftOpen, setIsLiftOpen] = useState(false);
  const [decide, { isLoading, originalArgs }] =
    useDecideModerationCaseMutation();
  const { author } = moderationCase;
  const activeBan = useActiveBan(author?.id ?? null);

  const name = author?.fullName.trim() || author?.email || "";
  const firstName = author?.firstName.trim() || name;
  const deciding = isLoading ? originalArgs?.state : undefined;

  const handleDecide = async (state: DecideCaseRequest["state"]) => {
    setError(null);
    try {
      await decide({
        caseId: moderationCase.id,
        state,
        publicFeedback:
          state === MODERATION_STATES.REJECTED ? feedback.trim() : "",
      }).unwrap();
      notification.success({
        message:
          state === MODERATION_STATES.APPROVED
            ? t("moderation.case.approved")
            : t("moderation.case.removed", { name: firstName }),
      });
    } catch (decideError) {
      const status = (decideError as { status?: number }).status;
      if (status === NOT_OPEN_STATUS) {
        notification.warning({ message: t("moderation.case.alreadyDecided") });
        return;
      }
      setError(t("moderation.case.decideError"));
    }
  };

  return (
    <>
      <div className="flex flex-col gap-100">
        <label
          htmlFor={feedbackId}
          className="moderation-muted body-xs-regular"
        >
          {t("moderation.case.feedbackLabel")}
        </label>

        <BaseTextArea
          id={feedbackId}
          value={feedback}
          autoSize={{ minRows: 2, maxRows: 5 }}
          placeholder={t("moderation.case.feedbackPlaceholder")}
          onChange={(event) => setFeedback(event.target.value)}
        />
      </div>

      {error && (
        <p className="moderation-error body-s-regular" role="alert">
          {error}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-200">
        {author &&
          (activeBan ? (
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              disabled={isLoading}
              onClick={() => setIsLiftOpen(true)}
            >
              {t("moderation.liftBan.button")}
            </BaseButton>
          ) : (
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              disabled={isLoading}
              onClick={() => setIsBanOpen(true)}
            >
              {t("moderation.ban.button")}
            </BaseButton>
          ))}

        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
          loading={deciding === MODERATION_STATES.REJECTED}
          disabled={isLoading}
          onClick={() => handleDecide(MODERATION_STATES.REJECTED)}
        >
          {t("moderation.case.remove")}
        </BaseButton>

        <BaseButton
          size={BUTTON_SIZES.SMALL}
          loading={deciding === MODERATION_STATES.APPROVED}
          disabled={isLoading}
          onClick={() => handleDecide(MODERATION_STATES.APPROVED)}
        >
          {t("moderation.case.approve")}
        </BaseButton>
      </div>

      <BanUserModal
        user={isBanOpen ? author : null}
        relatedCase={moderationCase.id}
        onClose={() => setIsBanOpen(false)}
      />

      <LiftBanModal
        sanction={isLiftOpen ? activeBan : null}
        userName={name}
        onClose={() => setIsLiftOpen(false)}
      />
    </>
  );
};

export default CaseActions;
